// npm modules
import React, { useState, useEffect, useRef } from 'react';
import { Alert } from 'react-bootstrap';
import { Link} from 'react-router-dom';
import { connect } from 'react-redux';
import validate from "validate.js";
import {isEmpty, isNil, omitBy} from "lodash";
import axios from "axios";
import randomstring from "randomstring";
import { parse as parseQuery } from "query-string";


// styles
import './logIn.css';
import styles from './login.module.css';

// icons & images
import {ReactComponent as GreenCheckmarkIcon} from '../images/green_checkmark.svg';

// actions
import { login, checkLogin, } from "../store/actions/authActions";
import {fetchDefaultOrg} from "../store/actions/organizationActions";

// components
import Loader from 'react-loader-spinner';
import { STATUSES } from "../serverVariables/statuses";
import TermsOfService from "./signUp/termsOfService/termsOfService";
import {useWindowSize} from "../hooks/useWindowSize";
import DaxkoSignup from "./daxkoSignup/daxkoSignup";
import {fetchDaxkoUser} from "../store/actions/organizationActions";
import {PrimaryButton, PrimaryOutlineButton} from "./buttons/buttons";
import ForgotPassword from './PasswordRecovery/ForgotPassword';

const pages = {
    LOGIN: 'login',
    SIGNUP_SELECTION: 'signup selection',
    SIGN_UP: 'sign up',
    DEMO: 'demo',
    FORGOT_PASSWORD: 'forgot password',  
};

const Login = (props) => {
    // state
    const { appState, user } = props;
    // props
    const { location, history } = props;
    // actions
    const { login, checkLogin, fetchDaxkoUser } = props;

    const [ email, setEmail ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ error, setError ] = useState('');
    const [ loading, setLoading ] = useState(true);
    const [policies, setPolicies] = useState([]);
    const {height} = useWindowSize();
    const tosRef = useRef();
    const [page, setPage] = useState(pages.LOGIN);
    const [orgId, setOrgId] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [ validation, setValidation ] = useState({});
    const [daxkoAuth, setDaxkoAuth] = useState(null);
    const [daxkoState] = useState(
        randomstring.generate({ length: 10, charset: "alphanumeric" })
    );
    const [returnedDaxkoState, setReturnedDaxkoState] = useState(null);
    const [defaultOrg, setDefaultOrg] = useState(null);

    //when appState is updated check status
    useEffect(() => {
        if (appState.isLoggedIn && appState.appReady && window.location.href.lastIndexOf('localhost') > 0) {
            const { from } = location.state || { from : null };
            if (from) {
                history.push(from);
            }
            if (location.state && location.state.linkTo) {
                history.push(location.state.linkTo);
                return;
            }
        }
    }, [ appState, history, location ]);

    useEffect(() => {
      if (returnedDaxkoState && inviteCode && orgId) {
        const stateCheck = parseQuery(props.location.search).state;
        if (returnedDaxkoState === stateCheck) {
          const code = parseQuery(props.location.search).code;
          fetchDaxkoUser(code)
            .then((res) => {
                history.push(`/signup?inviteCode=${inviteCode}&orgId=${orgId}&d_firstName=${res.first_name}&d_lastName=${res.last_name}&d_member=${res.daxko_member_id}`);
            })
            .catch((e) => console.log(e));
        }
      }
    }, [returnedDaxkoState, inviteCode, orgId]);

    // check if a user is logged in when mounting
    useEffect(() => {
        // check if a code was present
        const code = parseQuery(props.location.search).code;
        const state = parseQuery(props.location.search).state;
        
        if (code && state) {
            /* 
             * state validation for daxko
             * we compare the state returned from daxko with the one we stored in localStorage prior to the request
             * if they match we can continue
             */
            const savedState = localStorage.getItem('daxkoState');
            
            if (state === savedState) {
                setReturnedDaxkoState(state);

                const cookie2 = getCookie("daxkoOrgId");
                const cookie3 = getCookie("daxkoOrgCode");
                eraseCookie("daxkoOrgId");
                eraseCookie("daxkoOrgCode");
                setOrgId(cookie2);
                setInviteCode(cookie3);
                
                localStorage.removeItem('daxkoState');
                
                setLoading(true);
            } else {
                console.error('State validation failed - security risk');
                setError('Authentication failed. Please try again.');
                localStorage.removeItem('daxkoState');
            }
        }

        checkLogin()
            .then(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.body.style.background = '#fff';
        return () => document.body.style.background = null;
    },[policies]);

    // forward a user to the appropriate place
    useEffect(() => {
        if (appState.appReady && !appState.isLoggedIn && location && location.state && location.state.from && location.state.from.search) {
            history.push({search: location.state.from.search});
        }
        if (location.state && window.location.href.lastIndexOf('localhost') > 0) return;
        if (user && appState.isLoggedIn && appState.appReady) {
            if(user.permission_id === 1){
                if (user.user_status_id === STATUSES.unconfirmed_user) {
                    history.push('/member/onboard-confirm');
                }
                else if(user.user_status_id !== STATUSES.unconfirmed_user &&
                    user.user_status_id < STATUSES.onboarding_coach_consult_pending){
                    //client is in onboarding
                    history.push({
                        pathname : `/member/onboard-assessment`,
                    });
                }else{
                    // Forward to dashboard
                    let url = { pathname: `/member/clientDash/${user.username}` };

                    if (location.state && location.state.from && location.state.from.search){
                        url.search = location.state.from.search;
                    }

                    if (location.state && location.state.from) {
                    const fromPathname = location.state.from.pathname;

                    // Check if the URL matches /account/notifications
                    const pattern = /.*\/account\/notifications/gm;
                    const isNotifications = pattern  .test(fromPathname);

                    // Check if the URL matches /member/clientDash/{user.email}/assessments
                    const patternAssessment = /\/member\/clientDash\/[^/]+\/assessments/;
                    const isAssessments = patternAssessment.test(fromPathname);

                    if (isNotifications) {
                        url = location.state.from;
                    } else if (isAssessments) {
                        url = location.state.from;
                    }
                    }
                    history.push(url);
                }
            }else if(user.permission_id === 2){
                history.push({
                    pathname: '/dashboard/analytics/user'
                });
            } else if(user.permission_id === 5){
                history.push({
                    pathname: '/portal'  // redirect to customer portal if permission id 5
                });
            } else{
                history.push({
                    pathname:'/dashboard/analytics/user'
                });
            
            }
        }
    }, [ appState ]);

    // get default organization for individual signup
    useEffect(() => {
        async function run() {
            const org = await fetchDefaultOrg();
            console.log(org);
            setDefaultOrg(org);
        }

        try{
            run()
        } catch (e) {
            console.log(e);
        }
    }, []);

    function logInUser(e) {
        e.preventDefault();
        login({email, password})
            .then(val => {
                if (val && val.codeRequired) {
                    props.history.push('/mfa-code-verification');
                }
            })
            .catch(error => {
                setError('Invalid email or password');
        });
    }

    async function signupUser(e) {
        e.preventDefault();
        const errors = {};
        const constraints = {
            orgId: {
                type: {
                    type: 'integer',
                    message: '^Invalid Organization ID'
                },
                presence:{
                    message: '^Organization ID is required'
                },
            },
            inviteCode: {
                type:'string',
                presence:'^Organization code is required',
                length: {
                    minimum: 1,
                    maximum: 255,
                    tooShort: '^Organization code is required',
                    tooLong: '^Organization code must be less than 255 characters',
                }
            }
        };
        const validation = validate({ orgId: parseInt(orgId), inviteCode }, constraints);
        if (validation) {
            errors.orgId = validation.orgId;
            errors.inviteCode = validation.inviteCode;
        }
        setValidation(errors);
        if (!isEmpty(omitBy(errors, isNil))) return;

        try {
            // test to see if code is valid
            const { data: { result: valid } } = await axios.get(`/organizations/check/code/${orgId}/${inviteCode}`);

            setError('');

            if (valid) history.push(`/signup?inviteCode=${inviteCode}&orgId=${orgId}`);

        } catch (e) {
            if (e.response && e.response.data) {
                setError(e.response.data.error);
            } else {
                setError('Unable to verify code. Please contact your Treo administrator at support@treowellness.com');
            }
        }
    }

    function LoginForm() {
        return (
            <form onSubmit={logInUser} className={styles.formContainer}>
                <div className={styles.form}>
                    <p className={styles.formTitle}>Login</p>
                    {error ? <span className={'text-danger'}>{error}</span> : null}
                    <div>
                        <label htmlFor="email">Email</label>
                        <input type="text" id='email' name='email' value={email} onChange={e => setEmail(e.target.value)} data-testID='loginEmail'/>
                    </div>
                    <div>
                        <label htmlFor="password">Password</label>
                        <input type="password" id='password' name='password' value={password} onChange={e => setPassword(e.target.value)} data-testID='loginPass'/>
                    </div>
                    <div>
                        <div className={styles.formSubmit}>
                            <button className='btn primary' type='submit' onClick={logInUser}>Submit</button>
                            <a onClick={() => changePage(pages.FORGOT_PASSWORD)} className='link' style={{cursor: 'pointer'}}>
                            Forgot Password
                        </a>
                        </div>
                        <p>Need help? <a href="mailto:support@treowellness.com">support@treowellness.com</a></p>
                    </div>
                </div>
            </form>
        )
    }

    function SignUpForm() {
        return (
            <form className={styles.formContainer} onSubmit={signupUser}>
                <div className={styles.form}>
                    <div>
                        <p className={styles.formTitle2}>Account Sign Up</p>
                        <div className={styles.signupSubheading}>
                            <GreenCheckmarkIcon width={15} height={15} />
                            <span>I'm signing up through an organization</span>
                        </div>
                    </div>
                    {error ? <span className={'text-danger'}>{error}</span> : null}
                    <div>
                        <label htmlFor="orgId">Organization Id</label>
                        <input type="text" id='orgId' name='orgId' value={orgId} onChange={e => setOrgId(e.target.value)} data-testID='orgID'/>
                        {validation.orgId ? validation.orgId.map(err => <span className='text-danger'>{err}</span>) : null}
                    </div>
                    <div>
                        <label htmlFor="code">Code</label>
                        <input type="text" id='code' name='code' value={inviteCode} onChange={e => setInviteCode(e.target.value)} data-testID="code"/>
                        {validation.inviteCode ? validation.inviteCode.map(err => <span className='text-danger'>{err}</span>) : null}
                    </div>
                    <div className={styles.formSubmit}>
                        <div className={styles.signupSubmit}>
                            <PrimaryOutlineButton type='submit' onClick={() => changePage(pages.SIGNUP_SELECTION)}
                                    data-testID='submitBtn'>Back
                            </PrimaryOutlineButton>
                            <PrimaryButton type='submit' onClick={signupUser}
                                    data-testID='submitBtn'>Submit
                            </PrimaryButton>
                        </div>
                        <p>Need help? <a href="mailto:support@treowellness.com">support@treowellness.com</a></p>
                    </div>
                </div>

            </form>
        )
    }

    function SignupSelection() {
        return (
            <form className={styles.formContainer} onSubmit={signupUser}>
                <div className={styles.form}>
                    <p className={styles.formTitle2}>Tell us about yourself</p>

                    <div className={styles.selectionContainer}>
                        <PrimaryButton onClick={() => changePage(pages.SIGN_UP)}>I'm signing up through an organization</PrimaryButton>
                        {
                            defaultOrg ?
                                <PrimaryButton onClick={() => history.push(`/signup?inviteCode=${defaultOrg.code}&orgId=${defaultOrg.org_id}`)}>I'm signing up as an individual</PrimaryButton>
                                : null
                        }
                    </div>
                </div>

            </form>
        )
    }

    function Footer() {
        return (
            <div ref={tosRef} className={styles.formFooter}>
                <TermsOfService loadPolicies={setPolicies} horizontal/>
            </div>
        )
    }

    function changePage(page) {
        setError('')
        if (page === pages.DEMO) {
            return window.open(
                'https://treowellness.com/schedule-demo/',
                '_blank'
            );
        }
        if (page === pages.SIGN_UP) {
            setEmail('');
            setPassword('');
        }
        if (page === pages.LOGIN) {
            setOrgId('');
            setInviteCode('');
            setValidation({});
        }
        if (page === pages.SIGNUP_SELECTION) {
            setOrgId('');
            setInviteCode('');
            setValidation({});
        }
        setPage(page);
    }

    if (loading) {
        return (
            <div style={{height}} className='flex-vertically'>
                <Loader
                    type="Puff"
                    color="#354052"
                    height="100"
                    width="100"
                />
            </div>
        )
    }

    if (daxkoAuth) {
        return <DaxkoSignup scope={daxkoAuth} state={daxkoState}/>
    }

    return (
        <div className={styles.main}>
            <div className={styles.green}>
                <a href="/">
                <img className={styles.logo} src="https://treo.s3.us-east-2.amazonaws.com/assets/Logo_White.png"
                     alt="Logo"/>
                </a>
                <div className={styles.headingContainer}>
                    <h1 className={styles.heading1}>Change is Possible with Treo Wellness</h1>
                    <h2 className={styles.heading2}>We are your whole-person wellbeing app designed to help you discover
                        your healthy lifestyle. Get started today!</h2>
                </div>
            </div>
            <div className={styles.white}>
                <div className={styles.formSelector}>
                    <div className={styles.navLogin}>
                        <button onClick={() => changePage(pages.LOGIN)} className={`${styles.navBtn} ${page === pages.LOGIN ? styles.active : null}`}>Login</button>
                        <button onClick={() => changePage(pages.SIGNUP_SELECTION)} className={`${styles.navBtn} ${page === pages.SIGN_UP || page === pages.SIGNUP_SELECTION ? styles.active : null}`}>Sign Up</button>
                    </div>
                    {page === pages.LOGIN ? LoginForm() : null}
                    {page === pages.SIGNUP_SELECTION ? SignupSelection() : null}
                    {page === pages.SIGN_UP ? SignUpForm() : null}
                    {page === pages.FORGOT_PASSWORD && <ForgotPassword />}
                    {page === pages.FORGOT_PASSWORD ? null : Footer()}


                </div>
            </div>
        </div>
    )
}

function mapStateToProps({ appState, user }) {
    return {
        appState,
        user,
    }
}

const actions = {
    login,
    checkLogin,
    fetchDaxkoUser
}
export default connect(mapStateToProps, actions)(Login);

function createCookie(name, value) {
    let date = new Date();
    date.setTime(date.getTime() + 1000 * 60 * 5);
    const expires = "; expires=" + date.toGMTString();
    document.cookie = name + "=" + value + expires + "; path=/";
}

function getCookie(c_name) {
    if (document.cookie.length > 0) {
        let c_start = document.cookie.indexOf(c_name + "=");
        if (c_start !== -1) {
            c_start = c_start + c_name.length + 1;
            let c_end = document.cookie.indexOf(";", c_start);
            if (c_end === -1) {
                c_end = document.cookie.length;
            }
            return unescape(document.cookie.substring(c_start, c_end));
        }
    }
    return "";
}

function eraseCookie(name) {
    document.cookie = name + "=; Max-Age=-99999999;";
}
